<script setup>
import logoMain from '@/assets/images/logo-main.svg'
import { translate } from '@/plugins/i18n.js'
import { NO_INDEX_FOLLOW } from '@/services/helpers/MetaHelper.js'
import { useHead } from '@unhead/vue'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import TheForgotPassword from '../molecules/TheForgotPassword.vue'
import TheGuest from '../molecules/TheGuest.vue'
import TheLogin from '../molecules/TheLogin.vue'
import TheRegister from '../molecules/TheRegister.vue'
import TheTwoFactorAuth from '../molecules/TheTwoFactorAuth.vue'
import TheTwoFactorSetup from '../molecules/TheTwoFactorSetup.vue'

const emailRegex = /^[^@]+@[^@]+\.[^@]+$/
const logoSrc = logoMain

const emits = defineEmits(['success'])

const props = defineProps({
  useHardRedirects: {
    type: Boolean,
    default: false
  },
  allowedActions: {
    type: Array,
    default: () => [
      'login',
      'register',
      'forgot-password',
      'guest-order',
      'guest',
      'two-factor',
      'two-factor-setup'
    ]
  },
  messageTitle: {
    type: String,
    default: null
  },
  message: {
    type: String,
    default: null
  }
})

const allActions = {
  login: 1,
  register: 2,
  'forgot-password': 3,
  'two-factor': 4,
  'two-factor-setup': 5,
  'soft-login': 6,
  guest: 7
}

const actions = computed(() => {
  return Object.fromEntries(
    Object.entries(allActions).filter(([key]) =>
      props.allowedActions.includes(key)
    )
  )
})

const firstAction = computed(() => props.allowedActions[0] || 'login')
const slide = ref(actions.value[firstAction.value] || 1)

const currentView = computed(() => {
  switch (slide.value) {
    case actions.value['login']:
      return TheLogin
    case actions.value['register']:
      return TheRegister
    case actions.value['forgot-password']:
      return TheForgotPassword
    case actions.value['two-factor']:
      return TheTwoFactorAuth
    case actions.value['two-factor-setup']:
      return TheTwoFactorSetup
    case actions.value['soft-login']:
      return TheLogin
    case actions.value['guest']:
      return TheGuest
    default:
      return TheLogin
  }
})

const title = computed(() => {
  switch (slide.value) {
    case actions.value['login']:
      return 'login'
    case actions.value['register']:
      return 'header_navigation_register'
    case actions.value['forgot-password']:
      return 'reset_password'
    case actions.value['two-factor']:
      return 'two_factor_authentication_headline'
    case actions.value['two-factor-setup']:
      return 'two_factor_setup_headline'
    case actions.value['soft-login']:
      return 'soft_login'
    case actions.value['guest']:
      return 'order_as_guest'
    default:
      return 'login'
  }
})

const translatedTitle = computed(() => {
  if (!title.value) return null
  return translate(title.value)
})

if (props.useHardRedirects) {
  useHead({
    title: translatedTitle,
    meta: [
      {
        name: 'robots',
        content: NO_INDEX_FOLLOW
      }
    ]
  })
}

const route = useRoute()
const router = useRouter()

watch(
  () => route.params.action,
  (action) => {
    if (action && actions.value[action]) {
      slide.value = actions.value[action]
    }
  },
  { immediate: true }
)

const navigate = (action) => {
  if (actions.value[action]) {
    if (props.useHardRedirects) {
      const params = new URLSearchParams(route.query)
      router.push(`/auth/v2/${action}?${params}`)
    } else {
      slide.value = actions.value[action]
    }
  }
}
</script>

<template>
  <div
    class="d-flex flex-column py-5 px-sm-5 px-2 w-100"
    data-elbcontext="shopping:account"
  >
    <VRow>
      <div class="w-75 w-sm-50 mx-auto">
        <RouterLink to="/" class="align-center mb-5">
          <VImg
            itemscope
            itemtype="https://schema.org/Brand"
            height="100"
            :src="logoSrc"
            alt="vicampo"
          />
        </RouterLink>
      </div>
    </VRow>
    <VAlert
      v-if="props.message"
      id="auth-alert"
      prominent
      icon="custom:infoFilled"
      class="text-start mt-5"
      :title="props.messageTitle"
      :text="props.message"
    />
    <h2
      v-if="translatedTitle"
      class="py-5 font-size-xl font-weight-bold l-spacing-1 align-self-center"
    >
      {{ translatedTitle }}
    </h2>
    <VFadeTransition mode="out-in">
      <component
        :is="currentView"
        :key="slide"
        :email-regex="emailRegex"
        :allowed-actions="props.allowedActions"
        @showLogin="navigate('login')"
        @showRegister="navigate('register')"
        @showForgotPassword="navigate('forgot-password')"
        @showTwoFactor="
          (opts) =>
            navigate(opts?.setupRequired ? 'two-factor-setup' : 'two-factor')
        "
        @success="emits('success')"
        @showSoftLogin="navigate('soft-login')"
        @showGuest="navigate('guest')"
      />
    </VFadeTransition>
  </div>
</template>
